/**
 * Operator console data (`/admin`, BACKLOG A1/A3): query hooks for the admin routes plus the pure helpers the
 * pages share (fee grouping, wei formatting, bar scaling). Every admin route answers 403 to non-admins; the
 * pages guard on `Me.isAdmin` first and still render the 403 when the API refuses.
 */
import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import type { AdminUnreconciledFee } from '@algoboo/schema/api'
import type { AgentStatus, JobKind, JobStatus } from '@algoboo/schema'
import { request } from './api'

export const adminKeys = {
  root: () => ['admin'] as const,
  overview: () => ['admin', 'overview'] as const,
  agents: (status: string | null) => ['admin', 'agents', status] as const,
  users: () => ['admin', 'users'] as const,
  jobs: (status: string | null, kind: string | null) => ['admin', 'jobs', status, kind] as const,
  fees: () => ['admin', 'fees'] as const,
}

const pageOpts = {
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (last: { nextCursor: string | null }) => last.nextCursor ?? undefined,
}

export const adminOverviewQuery = () => queryOptions({ queryKey: adminKeys.overview(), queryFn: ({ signal }) => request('adminOverview', { signal }), refetchInterval: 30_000, retry: false })

export function useAdminOverview(enabled = true) {
  return useQuery({ ...adminOverviewQuery(), enabled })
}

export const adminAgentsQuery = (status: AgentStatus | undefined) =>
  infiniteQueryOptions({
    queryKey: adminKeys.agents(status ?? null),
    queryFn: ({ pageParam, signal }) => request('adminListAgents', { signal, query: { status, cursor: pageParam, limit: 50 } }),
    ...pageOpts,
    retry: false,
  })

export function useAdminAgents(status: AgentStatus | undefined, enabled = true) {
  return useInfiniteQuery({ ...adminAgentsQuery(status), enabled })
}

export const adminUsersQuery = () =>
  infiniteQueryOptions({
    queryKey: adminKeys.users(),
    queryFn: ({ pageParam, signal }) => request('adminListUsers', { signal, query: { cursor: pageParam, limit: 50 } }),
    ...pageOpts,
    retry: false,
  })

export function useAdminUsers(enabled = true) {
  return useInfiniteQuery({ ...adminUsersQuery(), enabled })
}

export const adminJobsQuery = (status: JobStatus | undefined, kind: JobKind | undefined) =>
  infiniteQueryOptions({
    queryKey: adminKeys.jobs(status ?? null, kind ?? null),
    queryFn: ({ pageParam, signal }) => request('adminListJobs', { signal, query: { status, kind, cursor: pageParam, limit: 50 } }),
    ...pageOpts,
    retry: false,
  })

export function useAdminJobs(status: JobStatus | undefined, kind: JobKind | undefined, enabled = true) {
  return useInfiniteQuery({ ...adminJobsQuery(status, kind), enabled })
}

export const adminFeesQuery = () => queryOptions({ queryKey: adminKeys.fees(), queryFn: ({ signal }) => request('adminListUnreconciledFees', { signal }), retry: false })

export function useAdminFees(enabled = true) {
  return useQuery({ ...adminFeesQuery(), enabled })
}

// Pure helpers ------------------------------------------------------------------------------

export interface FeeGroup {
  key: string
  agent: AdminUnreconciledFee['agent']
  category: AdminUnreconciledFee['category']
  rows: AdminUnreconciledFee[]
  /** Sum of `amountUsd`, exact to the micro-dollar. */
  totalUsd: string
  /** One `collect` call per row, newline-separated, ready to paste into the treasury runbook. */
  calls: string
}

const micro = (usd: string): bigint => {
  const [i = '0', f = ''] = usd.replace(/^-/, '').split('.')
  const v = BigInt(i) * 1_000_000n + BigInt((f + '000000').slice(0, 6))
  return usd.startsWith('-') ? -v : v
}
const usd = (m: bigint): string => `${m / 1_000_000n}.${(m % 1_000_000n).toString().padStart(6, '0')}`

/** Unreconciled fee rows grouped per agent and category (the API orders them so groups are contiguous). */
export function groupFees(rows: readonly AdminUnreconciledFee[]): FeeGroup[] {
  const groups = new Map<string, FeeGroup>()
  for (const r of rows) {
    const key = `${r.agent.id}:${r.category}`
    let g = groups.get(key)
    if (!g) {
      g = { key, agent: r.agent, category: r.category, rows: [], totalUsd: '0.000000', calls: '' }
      groups.set(key, g)
    }
    g.rows.push(r)
  }
  for (const g of groups.values()) {
    g.totalUsd = usd(g.rows.reduce((s, r) => s + micro(r.amountUsd), 0n))
    g.calls = g.rows.map((r) => r.instruction.call).join('\n')
  }
  return [...groups.values()]
}

/** Wei → ETH with up to `digits` fraction digits (trailing zeros trimmed). */
export function formatEth(wei: string | null | undefined, digits = 4): string {
  if (wei === null || wei === undefined) return '—'
  const v = BigInt(wei)
  const whole = v / 10n ** 18n
  const frac = (v % 10n ** 18n).toString().padStart(18, '0').slice(0, digits).replace(/0+$/, '')
  return `${whole}${frac ? `.${frac}` : ''} ETH`
}

/** Micro-dollars → "$1.25" (cents). */
export function formatMicroUsd(m: string): string {
  const v = BigInt(m)
  const cents = (v + 5_000n) / 10_000n
  return `$${(cents / 100n).toLocaleString('en-US')}.${(cents % 100n).toString().padStart(2, '0')}`
}

/** Bar length in percent of `max` (never below 2 % for a non-zero value so it stays visible). */
export function barPct(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0
  return Math.max(2, Math.round((value / max) * 1000) / 10)
}

/** Which job actions an operator may take (mirrors the API: retry failed/dead/cancelled, cancel queued). */
export function jobActions(status: JobStatus): { retry: boolean; cancel: boolean } {
  return { retry: status === 'failed' || status === 'dead' || status === 'cancelled', cancel: status === 'queued' }
}
